import React from 'react';
import type { AiThread } from '@lognai/plugin-ai-core';
import { Button } from '@lognai/synapse-design-system-v2';
import { useEntityLabel } from '../../hooks/use-entity-label';

export interface ThreadListProps {
  threads: readonly AiThread[];
  activeThreadId: string | null;
  isLoading?: boolean;
  onSelect: (threadId: string) => void;
  onNew: () => void;
}

const formatRelative = (input: Date | string): string => {
  const d = typeof input === 'string' ? new Date(input) : input;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

const humanize = (raw: string): string => {
  if (!raw) return raw;
  const spaced = raw.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced[0]!.toUpperCase() + spaced.slice(1);
};

interface ThreadRowProps {
  thread: AiThread;
  isActive: boolean;
  onSelect: (id: string) => void;
}

const ThreadRow: React.FC<ThreadRowProps> = ({ thread, isActive, onSelect }) => {
  const { label, isPending } = useEntityLabel({
    pluginId: thread.pinnedTo?.pluginId,
    entityType: thread.pinnedTo?.entityType,
    entityId: thread.pinnedTo?.entityId,
  });

  const entityTypeLabel = thread.pinnedTo ? humanize(thread.pinnedTo.entityType) : null;

  let title: string;
  if (thread.title && thread.title !== 'New conversation') {
    title = thread.title;
  } else if (thread.pinnedTo && entityTypeLabel) {
    if (label) {
      title = `${entityTypeLabel} ${label}`;
    } else if (isPending) {
      title = `${entityTypeLabel} · loading…`;
    } else {
      title = `${entityTypeLabel} · ${thread.pinnedTo.entityId.slice(0, 8)}`;
    }
  } else {
    title = `Chat · ${formatRelative(thread.lastMessageAt ?? thread.updatedAt)}`;
  }

  return (
    <button
      onClick={() => onSelect(thread.id)}
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 14px',
        border: 'none',
        borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
        background: isActive ? 'var(--primary-subtle)' : 'transparent',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: 14,
        color: 'var(--foreground)',
      }}
    >
      <div
        style={{
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </div>
      <div
        style={{
          color: 'var(--muted-foreground)',
          fontSize: 12,
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 3,
        }}
      >
        <span>
          {thread.messageCount} msg{thread.messageCount === 1 ? '' : 's'}
        </span>
        <span>{formatRelative(thread.lastMessageAt ?? thread.updatedAt)}</span>
      </div>
    </button>
  );
};

export const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  activeThreadId,
  isLoading,
  onSelect,
  onNew,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--foreground)' }}>Conversations</span>
        <Button variant="primary" size="sm" onClick={onNew}>+ New chat</Button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
        {isLoading ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>
            Loading conversations…
          </div>
        ) : threads.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>
            No conversations yet. Click "+ New chat" to start one.
          </div>
        ) : (
          threads.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              isActive={t.id === activeThreadId}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
};
